/**
 * useNativeAppVersion (T29.1) — the version of the NATIVE shell (the installed
 * APK / IPA), read through one cross-platform Capacitor call.
 *
 * On a native platform: `@capacitor/app` `App.getInfo()` returns the build's
 * `versionName` (`version`) and `versionCode` (`build`); `Capacitor.getPlatform()`
 * names the surface (`android` / `ios`). The identical `getInfo()` call returns
 * `CFBundleShortVersionString` / `CFBundleVersion` on iOS, so the iOS code path
 * comes for free — only the iOS native project + Xcode build-number script stay
 * future (D1 / D6).
 *
 * On web (no Capacitor shell): returns `null`. The `@capacitor/app` plugin is
 * native-only — calling it on web throws "not implemented". Two guards keep the
 * web bundle safe (AC #4):
 *   1. `Capacitor.isNativePlatform()` short-circuits before any plugin call, so
 *      the web path never touches `App.getInfo()`.
 *   2. The plugin is **dynamically imported** (only after the native check), so
 *      the eager web bundle never loads the native plugin module — same pattern
 *      as nativeGoogleSignIn.ts. A `catch → null` makes any failure (plugin
 *      missing, not-implemented) render as "no native version", never a crash.
 *
 * Every Capacitor import lives here in `apps/web` (the app layer may depend on
 * Capacitor); nothing Capacitor-shaped reaches `packages/shared` (CP1–CP4).
 */

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

export interface NativeAppVersion {
  /** `android` | `ios` — from `Capacitor.getPlatform()`. */
  platform: string;
  /** Native `versionName` (Android) / `CFBundleShortVersionString` (iOS). */
  version: string;
  /** Native `versionCode` (Android) / `CFBundleVersion` (iOS). */
  build: string;
}

/**
 * Read the native app version once. Resolves to `null` on web (or if the native
 * read fails) — never throws. Surfaces gate their native rows on a non-null
 * return, so a null reads as "no native shell" and the web behaves exactly as
 * T29 shipped.
 */
export async function readNativeAppVersion(): Promise<NativeAppVersion | null> {
  // Guard 1: never touch the native-only plugin on web.
  if (!Capacitor.isNativePlatform()) return null;
  try {
    // Guard 2: dynamic import keeps the plugin out of the eager web bundle; it
    // is only pulled in once we are confirmed native.
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return {
      platform: Capacitor.getPlatform(),
      version: info.version,
      build: info.build,
    };
  } catch {
    // Plugin missing / not-implemented / any failure — degrade to null so the
    // surface simply omits the native row instead of crashing.
    return null;
  }
}

export function useNativeAppVersion(): NativeAppVersion | null {
  const [native, setNative] = useState<NativeAppVersion | null>(null);

  useEffect(() => {
    let cancelled = false;
    void readNativeAppVersion().then((value) => {
      if (!cancelled) setNative(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return native;
}
